/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../../integrations/supabase/client';
import type { User, UserInvitation, UserRoleHistory, IUserRepository } from '../interfaces/IUserRepository';

export class SupabaseUserRepository implements IUserRepository {
  async getAll(clientId: string): Promise<User[]> {
    // Get profiles
    const { data: profiles, error: profileError } = await (supabase as any)
      .from('profiles')
      .select('*')
      .eq('client_id', clientId);

    if (profileError) throw profileError;
    if (!profiles || profiles.length === 0) return [];

    // Get user roles separately
    const userIds = profiles.map((p: any) => p.id);
    const { data: roles, error: rolesError } = await supabase
      .from('user_roles')
      .select('user_id, role')
      .in('user_id', userIds);

    if (rolesError) throw rolesError;

    // Create a map of user_id to role
    const roleMap = new Map(roles?.map(r => [r.user_id, r.role]) || []);

    return profiles.map((profile: any) => ({
      id: profile.id,
      email: profile.email,
      first_name: profile.full_name?.split(' ')[0],
      last_name: profile.full_name?.split(' ').slice(1).join(' '),
      phone: profile.phone,
      job_title: profile.job_title,
      role: roleMap.get(profile.id) || 'user',
      client_id: profile.client_id,
      status: profile.status || 'active',
      created_at: profile.created_at,
      last_login: profile.last_login,
    }));
  }

  async getAllByPlatformTenant(platformTenantId: string): Promise<User[]> {
    const { data, error } = await (supabase as any)
      .rpc('get_tenant_users', { _platform_tenant_id: platformTenantId });

    if (error) throw error;

    const users: any[] = Array.isArray(data) ? data : (data || []);
    if (users.length === 0) return [];

    return users.map((u: any) => ({
      id: u.id,
      email: u.email,
      first_name: u.full_name?.split(' ')[0],
      last_name: u.full_name?.split(' ').slice(1).join(' '),
      phone: u.phone,
      job_title: u.job_title,
      role: u.role || 'user',
      client_id: u.client_id,
      status: u.status || 'active',
      created_at: u.created_at,
      last_login: u.last_login,
    }));
  }

  async getById(id: string): Promise<User | null> {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select(`
        *,
        user_roles!inner(role)
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return {
      id: data.id,
      email: data.email,
      first_name: data.full_name?.split(' ')[0],
      last_name: data.full_name?.split(' ').slice(1).join(' '),
      phone: data.phone,
      job_title: data.job_title,
      role: (data.user_roles as any[])?.[0]?.role || 'user',
      client_id: data.client_id,
      status: data.status || 'active',
      created_at: data.created_at,
      last_login: data.last_login,
    };
  }

  async getByEmail(email: string): Promise<User | null> {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select(`
        *,
        user_roles!inner(role)
      `)
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return {
      id: data.id,
      email: data.email,
      first_name: data.full_name?.split(' ')[0],
      last_name: data.full_name?.split(' ').slice(1).join(' '),
      phone: data.phone,
      job_title: data.job_title,
      role: (data.user_roles as any[])?.[0]?.role || 'user',
      client_id: data.client_id,
      status: data.status || 'active',
      created_at: data.created_at,
      last_login: data.last_login,
    };
  }

  async searchUsers(clientId: string, query: string): Promise<User[]> {
    const { data, error } = await (supabase as any)
      .from('profiles')
      .select(`
        *,
        user_roles!inner(role)
      `)
      .eq('client_id', clientId)
      .or(`email.ilike.%${query}%,full_name.ilike.%${query}%`);

    if (error) throw error;

    return (data || []).map((profile: any) => ({
      id: profile.id,
      email: profile.email,
      first_name: profile.full_name?.split(' ')[0],
      last_name: profile.full_name?.split(' ').slice(1).join(' '),
      phone: profile.phone,
      job_title: profile.job_title,
      role: (profile.user_roles as any[])?.[0]?.role || 'user',
      client_id: profile.client_id,
      status: profile.status || 'active',
      created_at: profile.created_at,
      last_login: profile.last_login,
    }));
  }

  async inviteUser(data: {
    email: string;
    role: string;
    client_id: string;
    invited_by: string;
  }): Promise<UserInvitation> {
    const token = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

    const { data: invitation, error } = await (supabase as any)
      .rpc('create_user_invitation', {
        _email: data.email,
        _role: data.role,
        _client_id: data.client_id,
        _invited_by: data.invited_by,
        _token: token,
        _expires_at: expiresAt.toISOString(),
        _status: 'pending',
      });

    if (error) throw error;

    const inv = invitation as any;

    // Send invitation email via Edge Function
    try {
      const baseUrl = window.location.origin;
      const invitationLink = `${baseUrl}/accept-invitation?token=${token}`;

      const { error: emailError } = await supabase.functions.invoke('send-onboarding-invitation', {
        body: {
          recipientEmail: data.email,
          recipientName: data.email.split('@')[0],
          role: data.role,
          invitationLink,
          clientId: data.client_id,
          invitedBy: data.invited_by,
        },
      });

      if (emailError) {
        console.error('Failed to send invitation email:', emailError);
      }
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
    }

    return {
      id: inv?.id || token,
      email: inv?.email || data.email,
      role: inv?.role || data.role,
      invited_by: inv?.invited_by || data.invited_by,
      invited_at: inv?.invited_at || new Date().toISOString(),
      accepted: inv?.status === 'accepted',
      client_id: inv?.client_id || data.client_id,
    };
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const updateData: any = {};
    if (data.first_name || data.last_name) {
      updateData.full_name = `${data.first_name || ''} ${data.last_name || ''}`.trim();
    }
    if (data.status) updateData.status = data.status;
    if (data.last_login) updateData.last_login = data.last_login;

    const { data: profile, error } = await (supabase as any)
      .from('profiles')
      .update(updateData)
      .eq('id', id)
      .select(`
        *,
        user_roles!inner(role)
      `)
      .single();

    if (error) throw error;

    return {
      id: profile.id,
      email: profile.email,
      first_name: profile.full_name?.split(' ')[0],
      last_name: profile.full_name?.split(' ').slice(1).join(' '),
      role: (profile.user_roles as any[])?.[0]?.role || 'user',
      client_id: profile.client_id,
      status: profile.status || 'active',
      created_at: profile.created_at,
      last_login: profile.last_login,
    };
  }

  async deactivateUser(id: string, deactivated_by: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('profiles')
      .update({
        status: 'inactive',
        deactivated_by,
        deactivated_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) throw error;
  }

  async reactivateUser(id: string, _reactivatedBy: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('profiles')
      .update({
        status: 'active',
        deactivated_by: null,
        deactivated_at: null,
      })
      .eq('id', id);

    if (error) throw error;
  }

  async assignRole(userId: string, role: string, assigned_by: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('user_roles')
      .upsert({
        user_id: userId,
        role: role,
      });

    if (error) throw error;

    // Log to audit
    await supabase.from('audit_logs').insert({
      user_id: assigned_by,
      action: 'assign_role',
      resource_type: 'user',
      resource_id: userId,
      new_values: { role },
    });
  }

  async removeRole(userId: string, role: string, removed_by: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', role);

    if (error) throw error;

    // Log to audit
    await supabase.from('audit_logs').insert({
      user_id: removed_by,
      action: 'remove_role',
      resource_type: 'user',
      resource_id: userId,
      old_values: { role },
    });
  }

  async getRoleHistory(userId: string): Promise<UserRoleHistory[]> {
    const { data, error } = await supabase
      .from('audit_logs')
      .select('*')
      .eq('resource_id', userId)
      .in('action', ['assign_role', 'remove_role'])
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map((log: any) => ({
      id: log.id,
      user_id: userId,
      role: log.new_values?.role || log.old_values?.role,
      changed_by: log.user_id,
      changed_at: log.created_at,
      reason: log.metadata?.reason,
    }));
  }

  async getPendingInvitations(clientId: string): Promise<UserInvitation[]> {
    const { data, error } = await (supabase as any)
      .from('user_invitations')
      .select('*')
      .eq('client_id', clientId)
      .eq('status', 'pending');

    if (error) throw error;

    return (data || []).map((inv: any) => ({
      id: inv.id,
      email: inv.email,
      role: inv.role,
      invited_by: inv.invited_by,
      invited_at: inv.invited_at,
      accepted: inv.status === 'accepted',
      client_id: inv.client_id,
    }));
  }

  async resendInvitation(invitationId: string): Promise<void> {
    // Fetch invitation details to rebuild the email payload
    const { data: invitation, error: fetchError } = await (supabase as any)
      .from('user_invitations')
      .select('email, role, client_id, invited_by, token')
      .eq('id', invitationId)
      .single();

    if (fetchError || !invitation) {
      throw new Error(fetchError?.message || 'Invitation not found');
    }

    const baseUrl = window.location.origin;
    const invitationLink = `${baseUrl}/accept-invitation?token=${invitation.token}`;

    // Resend via the existing send-onboarding-invitation edge function
    const { error } = await supabase.functions.invoke('send-onboarding-invitation', {
      body: {
        recipientEmail: invitation.email,
        recipientName: invitation.email.split('@')[0],
        role: invitation.role,
        invitationLink,
        clientId: invitation.client_id,
        invitedBy: invitation.invited_by,
      },
    });

    if (error) throw error;
  }

  async cancelInvitation(invitationId: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('user_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) throw error;
  }
}
